import { Box, Grid, makeStyles } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ContentBlock from '../../../../../../components/Layout/ContentBlock';
import ContentBlockConsolidatedParam from '../../../../../../components/Layout/ContentBlockConsolidatedParam';
import ContentBlockConsolidatedYearValue from '../../../../../../components/Layout/ContentBlockConsolidatedYearValue';
import ContentBlockParam from '../../../../../../components/Layout/ContentBlockParam';
import HistoricalModal from '../../../../../../components/Modals/historicalModal';
import NoDataContainer from '../../../../../../components/NoDataContainer';
import { formatAddress } from '../../../../../../core/utils/formatter.utils';
import { AddressInfo, GeneralDetailResult } from '../../../../../../generated/model/models';
import {
  AddressHistory,
  columnsAddress,
  columnsAttribute,
  columnsCompany,
  columnsLegalForm,
  columnsStatus,
} from './HistoricalModals/HistoricalModalColumns';

export interface Props {
  data?: GeneralDetailResult;
}

const GeneralInformation: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const COMPANY_ADDRESS_FIELDS = ['address', 'city', 'country', 'postalCode', 'type'];

  const currentAddress = data?.addressDetails?.currentDetail
    ? formatAddress(COMPANY_ADDRESS_FIELDS, data?.addressDetails?.currentDetail[0])
    : '';

  const historyAddress = () => {
    const address: AddressHistory[] = [];
    data?.addressDetails?.historicDetail?.forEach((item: AddressInfo) => {
      address.push({
        name: formatAddress(COMPANY_ADDRESS_FIELDS, item),
        officialValidFrom: item.officialValidFrom,
        officialValidTo: item.officialValidTo,
      });
    });
    return address;
  };
  const companyNumbers = data?.generalDetails?.companyNumbers;
  const companyNumber = data?.generalDetails?.companyNumber;
  const companyNumberText =
    (companyNumbers && companyNumbers.length && companyNumbers) ||
    (companyNumber ? [companyNumber] : undefined);

  return (
    <Box className={classes.generalInfoBox}>
      <ContentBlock title={t(`companies:companyData:generalInfo`)}>
        {data && data.generalDetails ? (
          <Box className={classes.cellContainer}>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:companyName')}
                  value={data.generalDetails.currentName}
                >
                  <HistoricalModal
                    columns={columnsCompany}
                    data={data.namesHistory}
                    label={t('companies:showHistory')}
                    title={t('companies:companyData:companyNameHistoryTitle')}
                    translationsScope={'companies:companyData:companyHistory'}
                  />
                </ContentBlockParam>
                {data.generalDetails.currentOriginalFullName &&
                  data.generalDetails.currentOriginalFullName !==
                    data.generalDetails.currentName && (
                    <ContentBlockParam
                      label={t('companies:columns:originalFullName')}
                      value={data.generalDetails.currentOriginalFullName}
                    />
                  )}
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockParam label={t('companies:columns:address')} value={currentAddress}>
                  <HistoricalModal
                    columns={columnsAddress}
                    data={historyAddress()}
                    label={t('companies:showHistory')}
                    title={t('companies:companyData:addressHistoryTitle')}
                    translationsScope={'companies:companyData:addressHistory'}
                  />
                </ContentBlockParam>
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:country')}
                  value={data.generalDetails.country}
                />
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:emailAddress')}
                  values={data.generalDetails.emailAddresses}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:companyNumber')}
                  values={companyNumberText}
                />
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:website')}
                  values={data.generalDetails.websites}
                  isLink={true}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:status')}
                  values={data.generalDetails.currentStatuses}
                >
                  <HistoricalModal
                    columns={columnsStatus}
                    data={data.statusHistory}
                    label={t('companies:showHistory')}
                    title={t('companies:companyData:stausHistoryTitle')}
                    translationsScope={'companies:companyData:statusHistory'}
                  />
                </ContentBlockParam>
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:phone')}
                  values={data.generalDetails.phones}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:legalForm')}
                  value={data.generalDetails.currentLegalForm}
                >
                  <HistoricalModal
                    columns={columnsLegalForm}
                    data={data.legalFormHistory}
                    label={t('companies:showHistory')}
                    title={t('companies:companyData:legalFormHistoryTitle')}
                    translationsScope={'companies:companyData:legalFormHistory'}
                  />
                </ContentBlockParam>
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:socials')}
                  values={data.generalDetails.socials}
                  isLink={true}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockConsolidatedYearValue
                  label={t('companies:columns:employeeAverage')}
                  consolidated={data.generalDetails.consolidatedGeneralInfo?.employeeAverage}
                  unconsolidated={data.generalDetails.unconsolidatedGeneralInfo?.employeeAverage}
                />
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:vatNumber')}
                  value={data.generalDetails.vatNumber}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockConsolidatedYearValue
                  label={t('companies:columns:fte')}
                  consolidated={data.generalDetails.consolidatedGeneralInfo?.fte}
                  unconsolidated={data.generalDetails.unconsolidatedGeneralInfo?.fte}
                />
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockConsolidatedParam
                  label={t('companies:columns:financialYearEnd')}
                  consolidated={data.generalDetails.consolidatedGeneralInfo?.financialYearEnd}
                  unconsolidated={data.generalDetails.unconsolidatedGeneralInfo?.financialYearEnd}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:dateOfIncorporation')}
                  value={data.generalDetails.startDate}
                />
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockConsolidatedParam
                  label={t('companies:columns:typeOfAccounts')}
                  consolidated={data.generalDetails.consolidatedGeneralInfo?.typeOfAccounts}
                  unconsolidated={data.generalDetails.unconsolidatedGeneralInfo?.typeOfAccounts}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:cessationDate')}
                  value={data.generalDetails.cessationDate}
                />
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockConsolidatedParam
                  label={t('companies:columns:accountingStandard')}
                  consolidated={data.generalDetails.consolidatedGeneralInfo?.accountingStandard}
                  unconsolidated={data.generalDetails.unconsolidatedGeneralInfo?.accountingStandard}
                />
              </Grid>
            </Box>
            <Box className={classes.itemsBlock}>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:ticker')}
                  values={data.generalDetails.ticker}
                >
                  <HistoricalModal
                    columns={columnsAttribute}
                    data={data.tickerHistory}
                    label={t('companies:showHistory')}
                    title={t('companies:companyData:tickerHistoryTitle')}
                    translationsScope={'companies:companyData:attributeHistory'}
                  />
                </ContentBlockParam>
              </Grid>
              <Grid className={classes.item}>
                <ContentBlockParam
                  label={t('companies:columns:exchange')}
                  values={data.generalDetails.exchange}
                >
                  <HistoricalModal
                    columns={columnsAttribute}
                    data={data.exchangeHistory}
                    label={t('companies:showHistory')}
                    title={t('companies:companyData:exchangeHistoryTitle')}
                    translationsScope={'companies:companyData:attributeHistory'}
                  />
                </ContentBlockParam>
              </Grid>
            </Box>
          </Box>
        ) : (
          <NoDataContainer />
        )}
      </ContentBlock>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  generalInfoBox: {
    width: '45%',
    marginRight: theme.spacing(8),
    display: 'flex',
  },
  cellContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemsBlock: {
    display: 'flex',
    flexDirection: 'row',
  },
  item: {
    padding: theme.spacing(5),
    flexGrow: 1,
    flexBasis: 0,
  },
}));

export default GeneralInformation;
